import { Environment } from '../enums/Environment';

// DEPLOY SCALING PROFILES (the single source of truth for how big the cloud footprint is)
// Every deployable unit's size lives here, NOT scattered across the `cloud.deploy.path_*` fragments. The fragments carry
// `{SHARED-COMMON:DeployScaling:Active:...}` placeholders that `build:deploy` resolves from the ACTIVE profile below, so
// scaling the whole platform up or down is a ONE-LINE change (`ActiveDeployProfile`), not a fragment edit sweep. Values
// bake into `.deploy/**` at generate time; nothing is read at container runtime.
//
// WHAT EACH KNOB DOES
//   - minScale:        always-warm instance floor. >0 removes cold starts but bills 24/7. This is the biggest idle lever.
//   - maxScale:        burst ceiling. Costs nothing at idle; only raise/keep it high so load spikes never queue. Leave
//                      generous — cutting it is NOT a cost saving, it is a throughput cap.
//   - concurrency:     in-flight requests per instance. Higher = fewer instances for the same load (voice tolerates 80
//                      concurrent in-call webhooks per instance; static assets tolerate 250).
//   - cpuThrottling:   'false' = CPU allocated 24/7 (premium idle billing, sub-ms response) — reserve for the live
//                      in-call path. 'true' = CPU only during a request (cheaper, small cold-CPU delay) — fine for
//                      webhooks and dashboards. NOTE: 'true' is behaviourally identical to omitting the annotation.
//   - cpu/memory:      per-instance resources. CPU is the biggest per-instance cost driver when throttling is 'false'
//                      (allocated 24/7): one allocated vCPU costs ~$70/mo at Toronto rates, so a 2-vCPU always-on
//                      service bills ~$150/mo before RAM. Keep the floor profiles at 1 vCPU; right-size memory per role.
//   - dataVMs:         Compute Engine machine types for the always-on data tier (never scale to zero) — the single
//                      largest FIXED monthly cost. Right-sizing these is the biggest idle win.
//   - vpcConnector:    Serverless VPC Access connector instance range. GCP enforces a hard FLOOR of min_instances = 2 for
//                      the legacy connector, so `lean` cannot go below it here — documented, not a mistake.
//
// WHEN TO SCALE (flip ActiveDeployProfile one step up the ladder, or hand-tune a profile)
//   - The ladder is lean -> low -> standard -> high -> max. Each step is a pure superset of the one below: more warm instances, more burst headroom, bigger data VMs. Diagnose WHICH unit is
//     saturating before moving the whole ladder - bumping a single unit inside the active profile is cheaper and usually sufficient.
//   - lean   (launch floor):  up to ~10 tenants, ~20 concurrent calls, ~100 concurrent web visitors. Halves idle spend vs standard; single warm kong/automation instance.
//   - low    (first clients): lean + a second warm Kong instance. Removes the single-ingress-instance risk (a Kong replacement no longer drops incoming calls) at the cost of one always-CPU
//                             instance. Move here when real client calls start.
//   - standard (steady load): full redundancy/headroom. Move here when sustained load approaches lean/low headroom: > ~40 concurrent calls, > ~200 concurrent dashboard users, post-call queue
//                             depth staying non-zero, or data-VM CPU/RAM steady-state above ~60%.
//   - high   (heavy load):    double warm floors on every latency path, bigger data VMs (e2-standard-4). Move here at > ~150 concurrent calls or when standard's data tier saturates.
//   - max    (surge ceiling): quadruple warm floors, doubled burst ceilings, e2-standard-8 data tier. A crisis/surge preset, not a steady-state cost posture.
//   - Bump a single unit (edit that unit inside the active profile) before switching the whole profile - e.g. raise only
//     `automation.minScale` if the post-call queue backs up, or only a data VM's machine type if that DB saturates.
//   - NEVER lower `voice` below min 1 / cpuThrottling 'false': it is the live in-call latency path.
type DeployProfileName = 'standard' | 'lean' | 'low' | 'high' | 'max';

/** Named cloud footprint presets forming the scaling ladder: lean -> low -> standard -> high -> max. */
const DeployScalingProfiles = {
  // Full-headroom baseline: double-baseline (min 2) on the ingress gateway and the queue worker, allocated CPU on every
  // latency- or webhook-sensitive path, and 8 GB data VMs. Use when tenant/call/visitor load outgrows the lean envelope.
  standard: {
    services: {
      kong: { minScale: 2, maxScale: 12, concurrency: 200, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      voice: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      billing: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      lobby: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      user: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      automation: { minScale: 2, maxScale: 12, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      assets: { minScale: 1, maxScale: 6, concurrency: 250, cpuThrottling: 'true', cpu: '1', memory: '1Gi' }
    },
    dataVMs: { mongodb: 'e2-standard-2', postgres: 'e2-standard-2', redis: 'e2-small', seeder: 'e2-standard-2' },
    vpcConnector: { minInstances: 2, maxInstances: 3 }
  },
  // Cost-optimised floor for small production load (~10 tenants / ~20 concurrent calls / ~100 web visitors). Differences
  // from standard, each a pure IDLE cut that leaves burst (maxScale) untouched:
  //   - kong minScale 2->1 and automation minScale 2->1: one warm instance each is plenty at this load; drops two
  //     always-on instances (one of them the premium always-CPU kong).
  //   - billing cpuThrottling 'false'->'true': Stripe webhooks tolerate sub-second cold CPU; removes 24/7 allocated-CPU
  //     billing while staying warm (minScale 1).
  //   - every service 2 vCPU -> 1 vCPU: one allocated vCPU is ~$70/mo, so halving the two always-on units (kong, voice)
  //     saves ~$140/mo alone; concurrency 200 (kong) / 80 (voice) per instance is still generous headroom at this load.
  //   - data VMs 'e2-standard-2'->'e2-medium' (mongodb/postgres) and seeder ->'e2-small': 8 GB was provisioned for far
  //     more than 10 tenants; the seeder only runs backup loops.
  //   - voice untouched otherwise (min 1, cpuThrottling 'false', 1Gi): protects live in-call latency.
  lean: {
    services: {
      kong: { minScale: 1, maxScale: 12, concurrency: 200, cpuThrottling: 'false', cpu: '1', memory: '1Gi' },
      voice: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'false', cpu: '1', memory: '1Gi' },
      billing: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '512Mi' },
      lobby: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '512Mi' },
      user: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '512Mi' },
      automation: { minScale: 1, maxScale: 12, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '1Gi' },
      assets: { minScale: 1, maxScale: 6, concurrency: 250, cpuThrottling: 'true', cpu: '1', memory: '512Mi' }
    },
    dataVMs: { mongodb: 'e2-medium', postgres: 'e2-medium', redis: 'e2-small', seeder: 'e2-small' },
    vpcConnector: { minInstances: 2, maxInstances: 3 }
  },
  // lean + a second warm Kong instance: the only change is removing the single-ingress-instance window (a Kong restart/replacement on lean briefly drops ALL inbound traffic - including live
  // Vapi webhooks, i.e. incoming calls). Everything else stays at the lean floor. The first step to take when real client calls begin.
  low: {
    services: {
      kong: { minScale: 2, maxScale: 12, concurrency: 200, cpuThrottling: 'false', cpu: '1', memory: '1Gi' },
      voice: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'false', cpu: '1', memory: '1Gi' },
      billing: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '512Mi' },
      lobby: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '512Mi' },
      user: { minScale: 1, maxScale: 6, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '512Mi' },
      automation: { minScale: 1, maxScale: 12, concurrency: 80, cpuThrottling: 'true', cpu: '1', memory: '1Gi' },
      assets: { minScale: 1, maxScale: 6, concurrency: 250, cpuThrottling: 'true', cpu: '1', memory: '512Mi' }
    },
    dataVMs: { mongodb: 'e2-medium', postgres: 'e2-medium', redis: 'e2-small', seeder: 'e2-small' },
    vpcConnector: { minInstances: 2, maxInstances: 3 }
  },
  // Heavy steady load: double warm floors on every latency-sensitive path (voice included), allocated CPU on billing again, larger burst ceilings, and a 16 GB data tier. For sustained
  // > ~150 concurrent calls or a saturating standard data tier.
  high: {
    services: {
      kong: { minScale: 3, maxScale: 18, concurrency: 200, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      voice: { minScale: 2, maxScale: 12, concurrency: 80, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      billing: { minScale: 2, maxScale: 8, concurrency: 80, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      lobby: { minScale: 2, maxScale: 8, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      user: { minScale: 2, maxScale: 8, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      automation: { minScale: 3, maxScale: 18, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      assets: { minScale: 2, maxScale: 8, concurrency: 250, cpuThrottling: 'true', cpu: '2', memory: '2Gi' }
    },
    dataVMs: { mongodb: 'e2-standard-4', postgres: 'e2-standard-4', redis: 'e2-medium', seeder: 'e2-standard-2' },
    vpcConnector: { minInstances: 2, maxInstances: 6 }
  },
  // Surge ceiling: quadruple warm floors, doubled-again burst ceilings, 32 GB data tier, widest VPC connector range. A crisis/surge preset (storm season, marketing spike), not a steady-state
  // cost posture - step back down once the surge passes.
  max: {
    services: {
      kong: { minScale: 4, maxScale: 24, concurrency: 200, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      voice: { minScale: 4, maxScale: 24, concurrency: 80, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      billing: { minScale: 2, maxScale: 12, concurrency: 80, cpuThrottling: 'false', cpu: '2', memory: '2Gi' },
      lobby: { minScale: 2, maxScale: 12, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      user: { minScale: 2, maxScale: 12, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      automation: { minScale: 4, maxScale: 24, concurrency: 80, cpuThrottling: 'true', cpu: '2', memory: '2Gi' },
      assets: { minScale: 2, maxScale: 12, concurrency: 250, cpuThrottling: 'true', cpu: '2', memory: '2Gi' }
    },
    dataVMs: { mongodb: 'e2-standard-8', postgres: 'e2-standard-8', redis: 'e2-standard-2', seeder: 'e2-standard-2' },
    vpcConnector: { minInstances: 2, maxInstances: 10 }
  }
} as const;

/** THE SINGLE KNOB. Step up the ladder (lean -> low -> standard -> high -> max), then `pnpm build:deploy` + deploy. `low` is the first step once real client calls begin. */
const ActiveDeployProfile: DeployProfileName = 'lean';

/** Shared common constants for type union values, exported for use across all packages. */
export const CommonConstants = {
  Publisher: 'SKAR AI',

  // Time unit conversions
  // Conversion factor — multiply seconds by this to get milliseconds, or divide milliseconds by this to get seconds.
  MsPerSecond: 1000,
  // Conversion factor — multiply minutes by this to get seconds, or divide seconds by this to get minutes.
  SecPerMinute: 60,
  // Conversion factor — multiply hours by this to get minutes, or divide minutes by this to get hours.
  MinPerHour: 60,
  // Conversion factor — multiply days by this to get hours, or divide hours by this to get days.
  HrPerDay: 24,

  // Google Cloud Run deterministic project-hash suffix that appears in the default
  // `*.run.app` hostnames for every service in this project. Used to build the
  // internal (service-to-service) URIs that Kong proxies to, so a single change
  // updates every backend target when the project hash rotates (e.g. after a
  // project re-create or region migration). These hashes are NOT used for the
  // public-facing custom domains (`BaseProdURL`, `BaseKongProdURL`, etc.) — those
  // stay stable regardless of the underlying Cloud Run hash.
  BaseProdHash: '7unpyzvpqq',
  BaseTestHash: '46o4d3wouq',

  // Production base URL for the consolidated assets app. All prod app frontends
  // are mounted under this host as `<BaseProdURL>/web/<area>/<name>`. Swap to a custom
  // domain (e.g. `https://vertexecho.io`) when DNS is wired up — `GetSharedSettings.ts`
  // composes every Production frontend URI against this single value.
  // NOTE: Cloud Run URLs carry a deterministic project hash suffix (-lik7roe2eq-<region code>.a.run.app); the bare service-name form 404s at the Google Frontend.
  BaseProdURL: 'https://app.vertexecho.io',

  // Test base URL for the consolidated assets app. Mirrors `BaseProdURL` for the
  // test environment — every Test frontend URI in `GetSharedSettings.ts` is composed as
  // `<BaseTestURL>/web/<area>/<name>`.
  BaseTestURL: 'https://vertex-echo-infra-assets-test-46o4d3wouq-pd.a.run.app',

  // Production base URL for the Kong API gateway. Every Production service/API URI and
  // provider-webhook callback in `GetSharedSettings.ts` is composed against this single
  // host as `<BaseKongProdURL>/api/<service>`. Distinct from `BaseProdURL` — that is the
  // assets/frontend host; this is the gateway that fronts the backend services.
  BaseKongProdURL: 'https://api.vertexecho.io',

  // Test base URL for the Kong API gateway. Mirrors `BaseKongProdURL` for the test
  // environment — every Test service/API URI and provider-webhook callback is composed as
  // `<BaseKongTestURL>/api/<service>`.
  BaseKongTestURL: 'https://vertex-echo-infra-kong-test-46o4d3wouq-pd.a.run.app',

  // Defaults
  Defaults: {
    Localization: 'en-US',
    Timezone: 'America/Los_Angeles',
    PendingID: 'pending'
  },

  // Deploy scaling — resolved into `.deploy/**` by build:deploy. `Active` is the currently-selected profile; every
  // `cloud.deploy.path_*` fragment reads its size from `{SHARED-COMMON:DeployScaling:Active:...}`. See the profile
  // definitions and the `ActiveDeployProfile` knob above the CommonConstants export for the full what/when-to-scale.
  DeployScaling: {
    ActiveProfile: ActiveDeployProfile,
    Profiles: DeployScalingProfiles,
    Active: DeployScalingProfiles[ActiveDeployProfile]
  },

  // Keys for the tenant-defined custom Leads/Bookings terminology stored on BusinessProfile.customLabels. Shared so the dashboard, the niche-label hook, and the voice prompt builder all read and
  // write the same keys when a tenant's niche allows custom terms.
  CustomLabels: {
    Leads: 'leads',
    Bookings: 'bookings'
  },

  // Knowledge-base embeddings. The dimension is fixed by the embedding model (`OPENAI_EMBED_MODEL` = text-embedding-3-large -> 3072). It is the declared width of the `halfvec(N)` columns
  // (knowledge_chunks / core_knowledge_chunks) and the dimension a stored embedding must match to be written; vectors of any other length are stored as NULL (lexical-only). Columns are `halfvec`
  // (half-precision) rather than `vector` because pgvector's HNSW index caps the full-precision `vector` type at 2000 dimensions, while `halfvec` indexes up to 4000 - so 3072-d embeddings keep an
  // ANN index at the same storage cost as the old 1536-d full-precision column (2 bytes/dim). Changing the model's dimension here requires re-embedding every stored chunk and re-creating the
  // pgvector columns/indexes (a clean re-seed handles this pre-launch).
  Embedding: {
    Dimensions: 3072
  },

  // SaaS billing and usage-metering constants. Defaults here are safe baselines; per-plan
  // and per-tenant overrides live in settings/plan documents and are resolved at runtime.
  Billing: {
    // Minimum connected-call duration (seconds) for a call to count as a billable answered
    // call. Below this the call is treated as a misfire (immediate hang-up, wrong number)
    // and excluded from `answered_calls` metering. Overridable per plan.
    MinBillableCallSeconds: 15,
    // Trial length (days) granted to a new tenant on signup before the first invoice.
    TrialDays: 14,
    // Dunning grace window (days) a `past_due` subscription stays live before suspension.
    PastDueGraceDays: 7,
    // Voice minutes are billed rounded up to whole minutes; this is the rounding increment.
    VoiceMinuteRoundingSeconds: 60
  },

  // Voice call-answering guards: per-tenant concurrency soft cap and per-caller rate limiting. These bound both provider-pool fairness (one tenant cannot starve others of the shared Vapi account
  // concurrency) and abuse cost (a single caller / dialer cannot burn unbounded answered-call spend). The rate-limit ladder is operator-tunable via `global_settings.callRateLimits`; the values
  // here are the defaults applied when that override is absent.
  Voice: {
    // Fallback per-tenant concurrent-call cap when no plan value has been stamped onto the tenant yet (safety floor for a legacy/half-provisioned row). Real caps come from the plan on subscribe.
    DefaultConcurrentLimit: 2,
    // Redis TTL (seconds) on a per-call concurrency slot. Leak guard: a missed end-of-call-report cannot pin a slot forever. Matches the transient assistant's maxDurationSeconds hard call ceiling.
    ConcurrencySlotTTLSeconds: 900,
    // Headroom margin. A platform-wide SystemAlert is raised once total active concurrent calls reach (sum of all active tenant caps - this margin), so operators buy Vapi lines before callers hit
    // the wall.
    ConcurrencyHeadroomMargin: 2,
    // Fixed-window seconds for the two rate-limit windows.
    RateWindowSeconds: { Hour: 3600, Day: 86400 },
    // Caller rate-limit ladder defaults (per tenant), narrowest bucket first. Each tier is a fixed-window count for its subject. PerCaller/PerAreaCode apply to known caller numbers; Anonymous is a
    // shared per-tenant circuit breaker for withheld-ID calls (generous ceiling so it trips only under real abuse).
    RateLimits: {
      PerCaller: { hour: 4, day: 10 },
      PerAreaCode: { hour: 15, day: 40 },
      Anonymous: { hour: 10, day: 25 }
    }
  },

  // HTTP header parameter names.
  Params: {
    AuthHeader: 'authorization',
    Lang: 'lang',
    Limit: 'limit',
    Offset: 'offset',
    Since: 'since'
  },

  // Default pagination settings.
  Pagination: {
    Offset: 0,
    Limit: 50,
    // Hard ceiling on the page size any caller may request. Bounds the result set (and the per-page
    // memory/DB cost) regardless of the limit supplied on the query string.
    MaxLimit: 200
  },

  // Account password policy. Mirrors the Firebase Identity Platform password policy enforced server-side
  // (Firebase Console -> Authentication -> Settings -> Password policy):
  //   minLength = 6, maxLength = 48, requireNumeric = true, requireLowercase/Uppercase/NonAlphanumeric = false.
  // Client-side validation MUST match this configuration exactly so users get instant feedback before the round-trip.
  Password: {
    MinLength: 6,
    MaxLength: 48
  },

  // Username generation/validation policy. Used by the client default-username derivation in
  // `FirebaseAuthProvider.mapFirebaseUserToDTO` and the server collision-suffix step in `UserController.GatedLoginUser`.
  // - `MaxLength` is the absolute cap on the final stored username, including any collision suffix.
  // - `CollisionHashHexLength` = 4 hex chars (drawn from `crypto.randomBytes(2)` = 16 bits = 65536 buckets).
  // - `MaxLengthBeforeCollisionSuffix` = MaxLength - CollisionSeparator.length - CollisionHashHexLength.
  //   When a sanitized name collides with an existing username, the server truncates the base to this length
  //   before appending `${CollisionSeparator}${4hex}`, keeping the final username at exactly `MaxLength` chars.
  // - `Fallback` is used when sanitization yields an empty/too-short string or a result that does not start with a letter.
  Username: {
    MinLength: 3,
    MaxLength: 12,
    CollisionSeparator: '#',
    CollisionHashHexLength: 4,
    MaxLengthBeforeCollisionSuffix: 7,
    Fallback: 'User'
  },

  // Input-validation limits for data accepted from external or untrusted callers (in-call agent
  // tool arguments supplied by the voice/LLM provider, dashboard profile edits, tenant-user
  // registration, and billing-admin writes). Free-text fields are clamped (trimmed and truncated)
  // to these caps; format-critical fields (phone, email, ISO date) are rejected outright. Centralised
  // here so every service boundary enforces the same ceilings rather than scattering magic literals.
  Validation: {
    NameMaxLength: 200, // Person/business display names, customer names, lead names.
    EmailMaxLength: 320, // RFC 5321 maximum addressable email length.
    PhoneMaxLength: 16, // E.164: leading '+' plus up to 15 digits.
    ShortTextMaxLength: 500, // Single-line copy: greeting, tone, tagline, labels.
    NotesMaxLength: 2000, // Booking notes, lead reason, multi-line free text.
    SMSBodyMaxLength: 1600, // Twilio hard cap (10 concatenated 160-char GSM segments).
    AddressLineMaxLength: 200, // A single address/city/region line.
    PostalCodeMaxLength: 32,
    CountryCodeMaxLength: 8,
    URLMaxLength: 2048, // Common browser/CDN URL ceiling.
    CodeMaxLength: 64, // Plan code, niche slug, machine identifiers.
    IdempotencyKeyMaxLength: 256,
    MaxMinorAmount: 100000000, // 1,000,000.00 in minor units - sane upper bound for one money row.
    MaxQuantity: 1000000,
    MaxDurationMinutes: 1440, // A single booking window cannot exceed 24h.
    SignupStateMaxKeys: 40, // Public signup state: max distinct staged onboarding fields retained.
    SignupStateKeyMaxLength: 80, // Public signup state: max length of a single staged field name.
    SignupStateValueMaxLength: 1000, // Public signup state: each staged field value is clamped to this.
    SignupStateMaxTotalChars: 16000, // Public signup state: total key+value character budget across all fields.
    LogMetadataMaxKeys: 25, // Public log endpoint: max distinct metadata keys retained per entry.
    LogMetadataKeyMaxLength: 64, // Public log endpoint: max length of a single metadata key.
    LogMetadataValueMaxLength: 512, // Public log endpoint: each metadata string value is clamped to this.
    LogMetadataMaxTotalChars: 4000, // Public log endpoint: total key+value character budget across one entry's metadata.
    LogOriginMaxLength: 128, // Public log endpoint: max length of the client-supplied origin (it is part of the dedup hash, so an unbounded value inflates every stored row).
    LogVersionMaxLength: 64, // Public log endpoint: max length of the client-supplied build version.
    LogEnvironmentMaxLength: 32, // Public log endpoint: max length of the client-supplied environment label.
    BusinessHoursMaxWindows: 21, // Business profile: max weekly opening-hours windows (~3 split windows/day) folded into the prompt.
    BusinessServicesMaxCount: 50, // Business profile: max services folded into the prompt and stored in the JSONB column.
    KnowledgeDocumentsMaxCount: 50, // Knowledge base: hard cap on stored documents per tenant (upload + manual create). Enforced server-side; surfaced client-side as pre-flight feedback.
    // Business profile: tenant-authored call-handling instructions folded into the prompt as a dedicated section (well under the 24k-char total prompt budget; ~250 tokens per 1000 chars is
    // negligible in-call).
    AgentInstructionsMaxLength: 3000
  },

  // Database related constants.
  Database: {
    // User role constants for permission checks.
    UserRole: {
      Admin: 'admin',
      Employee: 'employee',
      None: 'none'
    },

    // Status constants.
    Status: {
      Active: 'active',
      Inactive: 'inactive'
    },

    // Self-service onboarding progress for a tenant. A new tenant advances through these gated steps in order and cannot reach the dashboard until `Complete`. `Account` is the implicit pre-tenant
    // step (our own data: tenant + owner + business profile); `Billing` requires a verified Stripe subscription; `Phone` requires a provisioned voice-provider number. Persisted on `tenants` so a
    // returning owner resumes at the exact step they left off.
    OnboardingStep: {
      Account: 'account',
      Billing: 'billing',
      Phone: 'phone',
      Complete: 'complete'
    },

    // Call lifecycle status. A forwarded missed call enters as `Ringing`, transitions to
    // `InProgress` once the voice agent answers, and ends `Completed` (graceful hang-up),
    // `Failed` (provider/agent error), or `Abandoned` (caller hung up before resolution).
    CallStatus: {
      Ringing: 'ringing',
      InProgress: 'in_progress',
      Completed: 'completed',
      Failed: 'failed',
      Abandoned: 'abandoned'
    },

    // Detected caller intent for a handled call. Drives downstream automation: `Booking`
    // attempts a scheduling action, `Lead` captures contact details for owner follow-up,
    // `Info` answers a knowledge-base question with no further action.
    CallIntent: {
      Booking: 'booking',
      Info: 'info',
      Lead: 'lead'
    },

    // Outcome status for a post-call action (booking, lead capture, owner notification).
    // `NeedsReview` flags actions the agent could not complete with confidence and that a
    // human at the tenant should reconcile.
    ActionStatus: {
      Pending: 'pending',
      Succeeded: 'succeeded',
      Failed: 'failed',
      NeedsReview: 'needs_review'
    },

    // Polymorphic link discriminator on leads: which interaction table the row's `call_id` points at - `Voice` = a phone call (calls.id), `Text` = an AI-texting conversation (texts.id).
    CallType: {
      Voice: 'voice',
      Text: 'text'
    },

    // Subscription/billing lifecycle status. Mirrors the canonical states surfaced by the
    // billing provider (Stripe) and gates tenant access: `Suspended`/`Canceled` revoke
    // call handling, `PastDue` warns but keeps service live during the dunning window.
    BillingStatus: {
      Trialing: 'trialing',
      Active: 'active',
      PastDue: 'past_due',
      Suspended: 'suspended',
      Canceled: 'canceled'
    },

    // Billing settlement currency. Fiat-only for the SaaS product (no crypto). ISO 4217 (lowercase, Stripe convention).
    BillingCurrency: {
      USD: 'usd',
      CAD: 'cad'
    },

    // Usage meter keys. Each key names a metered dimension aggregated per billing period
    // and reported to the billing provider for usage-based invoicing.
    MeterKey: {
      AnsweredCalls: 'answered_calls',
      VoiceMinutes: 'voice_minutes',
      // Meters EVERY message through the platform: owner notifications (SMS and email both ride it) and customer texting in both directions. UI copy presents it as "Messages".
      Messages: 'messages'
    },

    // Channel a call arrived through. `Forwarded` is the production path (carrier
    // conditional-call-forwarding into the provisioned number); `Direct` is a call placed
    // straight to the provisioned number; `Test` is a synthetic call from the dashboard.
    CallSourceChannel: {
      Forwarded: 'forwarded',
      Direct: 'direct',
      Test: 'test'
    },

    // Owner notification delivery channel. A tenant chooses how missed-call summaries reach
    // the owner: `SMS` (Twilio text) or `Email` (SendGrid transactional email).
    NotificationChannel: {
      SMS: 'sms',
      Email: 'email'
    },

    // AI-texting conversation channel. `SMS` is the only live channel; the constant list keeps
    // the column CHECK-constraintable so future channels (e.g. WhatsApp) extend it in one place.
    TextChannel: {
      SMS: 'sms'
    },

    // Direction of a single text message within a conversation: `Inbound` is customer-to-platform,
    // `Outbound` is platform-to-customer.
    TextDirection: {
      Inbound: 'inbound',
      Outbound: 'outbound'
    },

    // Who authored a text message: the AI agent, the tenant `Owner` (manual interject from the dashboard), the `System` (platform-generated notices such as opt-out confirmations), or the
    // `Customer` (every inbound message).
    TextSenderKind: {
      AI: 'ai',
      Owner: 'owner',
      System: 'system',
      Customer: 'customer'
    },

    // Scheduling provider slugs stored on `integrations.provider`. Google Calendar is the
    // primary integration; the rest connect through the same per-tenant OAuth redirect
    // flow. The backend resolves the concrete scheduling provider from this slug — the voice
    // agent never picks one directly.
    SchedulingProvider: {
      GoogleCalendar: 'google-calendar',
      MicrosoftOutlook: 'microsoft-outlook'
    },

    // Scope constants.
    Scope: {
      Dev: Environment.Development,
      DevTest: `${Environment.Development}-${Environment.Test}`,
      DevTestProd: `${Environment.Development}-${Environment.Test}-${Environment.Production}`
    },

    // IETF BCP 47 language tags (Best Current Practice 47).
    // Combines ISO 639 (language), ISO 3166 (country/region), and sometimes script codes (ISO 15924), e.g., zh-Hans for Simplified Chinese.
    // Format: <language>-<region> (e.g., en-US for US English, es-MX for Mexican Spanish).
    // One primary locale per language, with regional variants only where the speaker split is large
    // enough to matter (Spanish and Portuguese each carry an Americas and a Europe form).
    LocalizationCode: {
      enUS: 'en-US', // English - Source language (United States).
      esMX: 'es-MX', // Spanish - Most common LATAM dialect (Americas).
      esES: 'es-ES', // Spanish - Castilian Spanish (Europe).
      deDE: 'de-DE', // Germany - Major European market.
      frFR: 'fr-FR', // France - Standard European French.
      itIT: 'it-IT', // Italy - Standard Italian.
      nlNL: 'nl-NL', // Netherlands - Key European regulated market.
      svSE: 'sv-SE', // Sweden - Nordic regulated market.
      daDK: 'da-DK', // Denmark - Nordic market.
      fiFI: 'fi-FI', // Finland - Nordic market.
      noNO: 'no-NO', // Norway - Nordic market (Bokmål).
      elGR: 'el-GR', // Greece - Southern European language.
      plPL: 'pl-PL', // Poland - Major Eastern European market.
      csCZ: 'cs-CZ', // Czech Republic - Central European market.
      skSK: 'sk-SK', // Slovakia - Central European market.
      huHU: 'hu-HU', // Hungary - Central European language.
      roRO: 'ro-RO', // Romania - Eastern European language.
      bgBG: 'bg-BG', // Bulgaria - Eastern European language.
      hrHR: 'hr-HR', // Croatia - Balkan market.
      srRS: 'sr-RS', // Serbia - Balkan market.
      ukUA: 'uk-UA', // Ukraine - Eastern European language.
      ruRU: 'ru-RU', // Russia - Largest Eastern European language.
      trTR: 'tr-TR', // Turkey - Transcontinental language (Anatolia/Europe).
      ptBR: 'pt-BR', // Portuguese - Brazilian Portuguese (Americas).
      ptPT: 'pt-PT', // Portuguese - European Portuguese (Europe).
      zhHans: 'zh-Hans', // China - Chinese (Simplified Script), Asia-facing markets (Script tag used).
      jaJP: 'ja-JP', // Japan - High-value Asian market.
      koKR: 'ko-KR', // South Korea - Major Asian market.
      hiIN: 'hi-IN', // India - Hindi (largest South Asian language).
      urPK: 'ur-PK', // Pakistan - Urdu (RTL, large diaspora).
      bnBD: 'bn-BD', // Bangladesh - Bengali (major South Asian language).
      paIN: 'pa-IN', // India - Punjabi (large Canadian diaspora).
      idID: 'id-ID', // Indonesia - Indonesian (largest SE Asian market).
      filPH: 'fil-PH', // Philippines - Filipino/Tagalog (large service-sector diaspora).
      arSA: 'ar-SA', // Saudi Arabia - Common Middle Eastern Arabic standard.
      heIL: 'he-IL', // Israel - Hebrew (RTL).
      faIR: 'fa-IR', // Iran - Persian/Farsi (RTL, large diaspora).
      thTH: 'th-TH', // Thailand - Southeast Asian market.
      viVN: 'vi-VN', // Vietnam - Southeast Asian market.
      swKE: 'sw-KE', // Kenya - Swahili (East African lingua franca).
      papCW: 'pap-CW', // Curaçao - Papiamento (Curaçao; also spoken in Aruba and Bonaire). AN retired from ISO 3166-1 in 2010; CW is its successor.
      htHT: 'ht-HT', // Haiti - Haitian Creole (Haiti, large US/Canada diaspora).
      crsSC: 'crs-SC', // Seychelles - Seychellois Creole (Seychelles).
      mfeMU: 'mfe-MU' // Mauritius - Mauritian Creole (Mauritius).
    }
  }
} as const;
